const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://bhashik-backend.onrender.com';

import { getCurrentUser } from './auth';
import { secureApiFetch } from './secureApi';

// Update Bitts after a lesson (send only bittsEarned)
export const updateBitts = async (userId, bittsEarned) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.userId !== userId) {
      console.error('❌ User mismatch or not authenticated');
      return null;
    }

    return await secureApiFetch(`${BASE_URL}/api/users/update-bitts`, {
      method: 'POST',
      body: JSON.stringify({ userId, bittsEarned }),
    });
  } catch (err) {
    console.error('❌ Error updating bitts:', err);
    return null;
  }
};

// 📈 Fetch top 10 users for leaderboard
export const fetchLeaderboard = async () => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/leaderboard`);
    return await res.json();
  } catch (err) {
    console.error('❌ Error fetching leaderboard:', err);
    return [];
  }
};

// ✅ Fetch user profile by ID or 'me'
export const getUserProfile = async (userId) => {
  try {
    const currentUser = getCurrentUser();
    
    // Only allow fetching own profile or 'me'
    if (!currentUser || (userId !== 'me' && userId !== currentUser.userId)) {
      console.error('❌ User mismatch or not authenticated');
      return null;
    }

    const url = `${BASE_URL}/api/users/${userId === 'me' ? 'me' : userId}`;
    const data = await secureApiFetch(url, { method: 'GET' });

    if (!data) {
      console.error("❌ No data returned from profile fetch");
      return null;
    }

    // Validate that the returned profile matches the current user
    if (data._id !== currentUser.userId) {
      console.error("❌ Profile user ID mismatch");
      return null;
    }

    return data;
  } catch (err) {
    console.error('❌ Error fetching user profile:', err);
    return null;
  }
};

// Login
export const loginUser = async (name, password) => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, password }),
    });
    const data = await res.json();
    if (data.token) {
      localStorage.setItem('token', data.token); // Save the token!
    }
    return data;
  } catch (err) {
    console.error('❌ Login error:', err);
  }
};

// Register new user
export const registerUser = async (userData) => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return await res.json();
  } catch (err) {
    console.error('❌ Registration error:', err);
  }
};

// Update profile info (standard, district, taluka)
export const updateUserProfile = async (userId, updates) => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/update/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return await res.json();
  } catch (err) {
    console.error('❌ Profile update error:', err);
  }
};

// Update LessonsCompleted after a lesson
export const updateLessonProgress = async (userId, lessonKey, progress, groupNumber = null) => {
  try {
    console.log('[API] updateLessonProgress - Input:', { userId, lessonKey, progress, groupNumber });
    
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.userId !== userId) {
      console.error('[API] ❌ User mismatch or not authenticated');
      return null;
    }

    // Ensure lessonKey is a string (either "lesson1" format or just the number)
    let lessonNumber;
    let finalLessonKey;
    if (typeof lessonKey === 'string') {
      lessonNumber = parseInt(lessonKey.replace('lesson', ''));
      finalLessonKey = lessonKey; // Use the provided string format
    } else {
      lessonNumber = lessonKey; // It's already a number
      finalLessonKey = `lesson${lessonNumber}`; // Convert to string format
    }
    
    if (isNaN(lessonNumber)) {
      throw new Error(`Invalid lesson key: ${lessonKey}`);
    }

    // Build and validate payload in the format expected by updateProgress controller
    const payload = {
      userId: currentUser.userId,
      LessonsCompleted: {
        [finalLessonKey]: {
          ...progress,
          timestamp: Date.now(),
          completed: true // Mark as completed when updating progress
        }
      }
    };
    
    // Add groupNumber if provided (for group-wise structure)
    if (groupNumber) {
      payload.groupNumber = groupNumber;
      console.log('[API] Using group-wise lesson structure for group', groupNumber);
    } else {
      console.log('[API] Using flat lesson structure (no groupNumber provided)');
    }

    // Validate all required fields are present
    if (!payload.userId || !payload.LessonsCompleted) {
      throw new Error('Missing required fields in payload');
    }

    console.log('[API] updateLessonProgress - Sending payload:', JSON.stringify(payload, null, 2));
    
    const response = await secureApiFetch(`${BASE_URL}/api/users/update-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-ID': currentUser.userId // Add userId to headers for extra validation
      },
      body: JSON.stringify(payload)
    });

    console.log('[API] updateLessonProgress - Response:', response);
    
    if (!response || !response.success) {
      console.error('[API] updateLessonProgress - Failed response:', response);
      throw new Error(`Failed to update lesson progress: ${response?.error || 'Unknown error'}`);
    }
    
    return response;
  } catch (err) {
    console.error('[API] ❌ Error updating LessonsCompleted:', err);
    throw err; // Rethrow to be handled by caller
  }
};

// ✅ Update dailyGoals in MongoDB
export const updateDailyGoals = async (userId, newGoals) => {
  try {
    console.log('API updateDailyGoals: Starting update with:', { userId, newGoals });
    return await secureApiFetch(`${BASE_URL}/api/users/update-daily-goals`, {
      method: 'POST',
      body: JSON.stringify({
        earnedBitts: newGoals.earnedBitts || 0,
        perfectLessons: newGoals.perfectLessons || 0,
        practiceMinutes: newGoals.practiceMinutes || 0
      })
    });
  } catch (err) {
    console.error('❌ Error updating daily goals:', err);
    throw err; // Re-throw to allow caller to handle
  }
};// ✅ Update wordStats in MongoDB - Supports both old and new structures
export const updateWordStats = async (userId, wordStats, groupNumber = null) => {
  try {
    console.log('[API] updateWordStats - Input:', { userId, groupNumber, wordCount: Object.keys(wordStats || {}).length });
    
    // Validate the input
    if (!userId) {
      console.warn('[API] Missing userId');
      return { success: false, error: 'Missing userId' };
    }
    if (!wordStats || typeof wordStats !== 'object') {
      console.warn('[API] Invalid wordStats format:', wordStats);
      return { success: false, error: 'Invalid word stats format' };
    }

    // Prepare payload - groupNumber is optional for backward compatibility
    const payload = { userId, wordStats };
    if (groupNumber) {
      payload.groupNumber = groupNumber;
      console.log('[API] Using group-wise structure for group', groupNumber);
    } else {
      console.log('[API] Using flat structure (no groupNumber provided)');
    }
    
    console.log('[API] updateWordStats - Sending request to server');

    const response = await secureApiFetch(`${BASE_URL}/api/users/update-wordstats`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    console.log('[API] updateWordStats - Response:', response);
    return response;
  } catch (err) {
    console.error('[API] ❌ Error updating word stats:', err.message);
    console.error('[API] Error stack:', err.stack);
    throw err;
  }
};

// ✅ Update compact word stats in MongoDB - Uses efficient compact format
export const updateCompactWordStats = async (userId, compactWordStats) => {
  try {
    console.log('[API][DEBUG] updateCompactWordStats - Input:', { 
      userId, 
      compactStatsCount: Object.keys(compactWordStats || {}).length,
      hasStats: compactWordStats !== null && typeof compactWordStats === 'object',
      topLevelKeys: Object.keys(compactWordStats || {})
    });
    
    // Validate the input
    if (!userId) {
      console.warn('[API][ERROR] Missing userId');
      return { success: false, error: 'Missing userId' };
    }
    if (!compactWordStats || typeof compactWordStats !== 'object') {
      console.warn('[API][ERROR] Invalid compactWordStats format:', compactWordStats);
      return { success: false, error: 'Invalid compact word stats format' };
    }

    console.log('[API][DEBUG] Raw stats structure before processing:', JSON.stringify(compactWordStats).substring(0, 500));

    // CRITICAL FIX: Make sure we send the correct format to the server
    // Extract just the group structure that's needed by the server
    // The server expects: { group1: { I: { G1-1IC: 1, G1-2IC: 1 }, ... } }
    
    // First check if there are any group keys at all
    const groupKeys = Object.keys(compactWordStats).filter(key => key.startsWith('group'));
    
    // If no group keys exist at all, we need to initialize at least one group structure
    if (groupKeys.length === 0) {
      console.warn('[API][CRITICAL] No group structures found in stats! Creating default group1 structure');
      
      // Extract group number from any G1-X keys if they exist
      let groupNum = 1; // Default to group 1
      const wordCodeKeys = Object.keys(compactWordStats).filter(key => key.match(/^G\d+-\d+$/));
      if (wordCodeKeys.length > 0) {
        const match = wordCodeKeys[0].match(/^G(\d+)-\d+$/);
        if (match) {
          groupNum = parseInt(match[1], 10);
          console.log(`[API][DEBUG] Extracted group number ${groupNum} from word code ${wordCodeKeys[0]}`);
        }
      }
      
      // Initialize empty group structure
      compactWordStats[`group${groupNum}`] = {
        'I': {}, // imagewords
        'G': {}, // meaningword
        'P': {}, // matchimages
        'M': {}, // matchwords
        'S': {}, // spellingword
        'L': {}, // listening
        'K': {}  // speakingword
      };
      
    // Transfer any direct key-value pairs to this structure
    Object.keys(compactWordStats).forEach(key => {
      if (key.match(/^G\d+-\d+[IGPMSKLCT]C$/)) {
        // This is a direct stat key like G1-1IC
        const match = key.match(/^G\d+-\d+([IGPMSKLCT])C$/);
        if (match) {
          const qType = match[1];
          compactWordStats[`group${groupNum}`][qType][key] = compactWordStats[key];
          console.log(`[API][DEBUG] Transferred direct key ${key} to group${groupNum}.${qType}.${key}`);
          
          // Remove the direct key to prevent duplicates
          delete compactWordStats[key];
          console.log(`[API][DEBUG] Removed direct key ${key} to prevent duplicate storage`);
        }
      }
    });      console.log(`[API][DEBUG] Created group${groupNum} structure:`, 
        Object.keys(compactWordStats[`group${groupNum}`]));
    }
    
    // Check if all group structures have the proper format
    const updatedGroupKeys = Object.keys(compactWordStats).filter(key => key.startsWith('group'));
    updatedGroupKeys.forEach(groupKey => {
      const groupData = compactWordStats[groupKey];
      
      // If group data isn't an object or is empty, initialize it
      if (!groupData || typeof groupData !== 'object' || Object.keys(groupData).length === 0) {
        console.warn(`[API][DEBUG] Group ${groupKey} has invalid structure. Creating question type containers.`);
        compactWordStats[groupKey] = {
          'I': {}, // imagewords
          'G': {}, // meaningword
          'P': {}, // matchimages
          'M': {}, // matchwords
          'S': {}, // spellingword
          'L': {}, // listening
          'K': {}  // speakingword
        };
        return;
      }
      
      // Check if each question type exists
      const requiredTypes = ['I', 'G', 'P', 'M', 'S', 'L', 'K'];
      requiredTypes.forEach(qType => {
        if (!groupData[qType]) {
          console.warn(`[API][DEBUG] Group ${groupKey} missing question type ${qType}. Creating it.`);
          groupData[qType] = {};
        }
      });
    });
    
    // Now filter out any non-group keys for what we send to the server
    let statsToSend = {};
    updatedGroupKeys.forEach(groupKey => {
      statsToSend[groupKey] = compactWordStats[groupKey];
    });
    
    console.log('[API][DEBUG] Final stats structure to send has these groups:', Object.keys(statsToSend));
    
    // Verify we have at least one group with proper structure
    if (Object.keys(statsToSend).length === 0) {
      console.error('[API][ERROR] No valid group structures found after processing!');
      return { success: false, error: 'No valid group structures in stats' };
    }
    
    // CRITICAL FIX: Remove any direct word code keys (G1-1 format) to avoid duplicates
    // Since we're already storing this data in the group structure
    Object.keys(statsToSend).forEach(key => {
      if (key.match(/^G\d+-\d+$/)) {
        console.log(`[API][DEBUG] Removing duplicate word structure ${key} to prevent double storage`);
        delete statsToSend[key];
      }
    });
    
    // Sample the structure for validation
    const firstGroupKey = Object.keys(statsToSend)[0];
    console.log(`[API][DEBUG] Structure check for ${firstGroupKey}:`, {
      hasGroup: !!statsToSend[firstGroupKey],
      isObject: typeof statsToSend[firstGroupKey] === 'object',
      questionTypes: statsToSend[firstGroupKey] ? Object.keys(statsToSend[firstGroupKey]) : 'none',
      firstQType: statsToSend[firstGroupKey] ? 
        Object.keys(statsToSend[firstGroupKey])[0] : 'none'
    });
    
    // Prepare payload with the filtered data
    const payload = { userId, compactWordStats: statsToSend };
    
    console.log('[API][DEBUG] updateCompactWordStats - Sending request to server');
    console.log('[API][DEBUG] Stats payload structure:', JSON.stringify(statsToSend, null, 2).substring(0, 500) + '...');

    console.log('[API][DEBUG] Sending request to:', `${BASE_URL}/api/users/update-compact-wordstats`);
    const startTime = Date.now();
    
    try {
      const response = await secureApiFetch(`${BASE_URL}/api/users/update-compact-wordstats`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      const duration = Date.now() - startTime;
      console.log(`[API][DEBUG] Request completed in ${duration}ms`);
      console.log('[API][DEBUG] updateCompactWordStats - Response status:', response.success ? 'SUCCESS' : 'FAILED');
      
      if (response.success === false) {
        console.error('[API][ERROR] Server returned error:', response.error);
      } else if (response.compactWordStats) {
        console.log('[API][DEBUG] Server returned updated stats with structure:', {
          topLevelKeys: Object.keys(response.compactWordStats || {}),
          hasGroup1: response.compactWordStats?.group1 ? 'Yes' : 'No',
          group1Types: response.compactWordStats?.group1 ? Object.keys(response.compactWordStats.group1) : 'N/A'
        });
      }
      
      return response;
    } catch (err) {
      const duration = Date.now() - startTime;
      console.error(`[API][ERROR] ❌ Error updating compact word stats after ${duration}ms:`, err.message);
      console.error('[API][ERROR] Error stack:', err.stack);
      console.error('[API][ERROR] Did the request reach the server? Check network tab and server logs');
      throw err;
    }
  } catch (err) {
    console.error('[API][ERROR] ❌ Fatal error in updateCompactWordStats:', err.message);
    console.error('[API][ERROR] Error stack:', err.stack);
    throw err;
  }
};

export const updateSentenceStats = async (userId, sentenceStats) => {
  try {
    console.log('[API] updateSentenceStats - Input:', { userId, rawSentenceStats: sentenceStats });
    
    // Validate the input
    if (!userId) {
      console.warn('[API] Missing userId');
      return { success: false, error: 'Missing userId' };
    }
    if (!sentenceStats || typeof sentenceStats !== 'object') {
      console.warn('[API] Invalid sentenceStats format:', sentenceStats);
      return { success: false, error: 'Invalid sentence stats format' };
    }

    // Send both userId and sentence stats - backend expects both in request body
    const payload = { userId, sentenceStats };
    console.log('[API] updateSentenceStats - Sending payload:', JSON.stringify(payload, null, 2));

    const response = await secureApiFetch(`${BASE_URL}/api/users/update-sentencestats`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    console.log('[API] updateSentenceStats - Response:', response);
    return response;
  } catch (err) {
    console.error('[API] ❌ Error updating sentence stats:', err.message);
    console.error('[API] Error stack:', err.stack);
    throw err;
  }
};

// ✅ Consolidated lesson-complete — single API call replacing 5 separate post-lesson calls.
// Sends lessonKey, stats, bitts, and daily-goals delta; server merges and saves atomically.
export const lessonComplete = async (payload) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('[API] lessonComplete: not authenticated');
      return null;
    }
    return await secureApiFetch(`${BASE_URL}/api/users/lesson-complete`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[API] ❌ lessonComplete error:', err);
    return null;
  }
};

// Advance to the next vocab group
export const advanceGroup = async (userId) => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/advance-group/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    // Check for non-2xx status before trying to parse JSON
    if (!res.ok) {
      // Optionally log: console.error(`❌ advanceGroup failed: ${res.status} ${res.statusText}`);
      return null; // Return null or a custom object to signal failure
    }

    // Try/catch here in case backend returns HTML error page
    try {
      return await res.json();
    } catch (jsonErr) {
      // Optionally log: console.error('❌ Error parsing advanceGroup JSON:', jsonErr);
      return null;
    }
  } catch (err) {
    console.error('❌ Error advancing group:', err);
    return null;
  }
};
// Get all unlocked groups for a user
export const getUnlockedGroups = async (userId) => {
  try {
    const res = await fetch(`${BASE_URL}/api/users/unlocked-groups/${userId}`);
    return await res.json();
  } catch (err) {
    console.error('❌ Error fetching unlocked groups:', err);
    return [];
  }
};

// Update user group (and/or unlockedGroups) via update-progress
export const updateUserGroup = async (userId, currentGroup, unlockedGroups) => {
  try {
    console.log('[API] updateUserGroup called with:', { userId, currentGroup, unlockedGroups });
    // Use update-progress which supports setting currentGroup and unlockedGroups explicitly
    const payload = {
      userId,
      currentGroup,
      unlockedGroups: Array.isArray(unlockedGroups) ? unlockedGroups : undefined
    };
    const res = await secureApiFetch(`${BASE_URL}/api/users/update-progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    // secureApiFetch returns the parsed JSON object
    console.log('[API] updateUserGroup response:', res);
    if (!res || res.success === false) {
      const msg = res?.error || 'Failed to update user group';
      console.error('[API] updateUserGroup failed:', msg);
      throw new Error(msg);
    }
    return res;
  } catch (err) {
    console.error('❌ Error updating user group:', err);
    throw err;
  }
};
